import { useRef } from 'react';
import type { VoidFunctionComponent } from 'react';
import IssuesTable from './IssuesTable';
import IssuesPagination from './IssuesPagination';
import IssuesFilters from '../filters/IssuesFilters';
import useSyncStateAndURLParams from './hooks/useSyncStateAndURLParams';
import { window } from '../../../globals';
import { useDispatchOnMount } from '../../../hooks';
import OrganizationsStore from '../../../redux/organizations';
import { SidebarLayout } from '../../layouts';
import IssueDetailsDrawer from '../IssueDetails/Drawer';
import type { IssuesContainerProps } from '../IssuesContainer';
import IssuesInboxHeader from './IssuesInboxHeader';
import './IssuesInbox.less';

/**
 * IssuesInbox is a view of all the issues, along with the filters
 * to allow users to find and take action on the issues which matter to them.
 * Depending on the Scope, they can be viewing issues for a given project,
 * or all issues across an org (ie: "global").
 */
const IssuesInbox: VoidFunctionComponent<IssuesContainerProps> = ({
  router,
  location,
  params,
}) => {
  // We need to fetch the organization in order to load Jira's info.
  useDispatchOnMount(() => OrganizationsStore.actions.fetchOne(window.user?.organizationId as number));

  const tableRef = useRef<HTMLDivElement>(null);

  //  Handles keeping the URL params and state in sync.
  //  Note that on the initial render this will always dispatch the
  //  HYDRATE_STATE action, which will kick off the initial fetch of issues.
  useSyncStateAndURLParams({ router, location, params });

  return (
    <div className="issues-inbox" data-test-id="issues-inbox">
      <SidebarLayout>
        <SidebarLayout.Header>
          <IssuesInboxHeader />
        </SidebarLayout.Header>
        <SidebarLayout.Main>
          <IssuesTable tableRef={tableRef} />
          <IssuesPagination />
        </SidebarLayout.Main>
        <SidebarLayout.Sidebar>
          <IssuesFilters />
        </SidebarLayout.Sidebar>
      </SidebarLayout>
      <IssueDetailsDrawer clickTarget={tableRef} category={params.category} />
    </div>
  );
};

export default IssuesInbox;
